import { Sofa, Building2, DraftingCompass, KeyRound, Box, ArrowRight } from "lucide-react";
import PageHero, { ScrollCta } from "@/components/PageHero";
import { Reveal } from "@/components/Reveal";
import LeadForm from "@/components/LeadForm";

const IMG = {
  residential: "https://static.prod-images.emergentagent.com/jobs/946b3673-7a0e-484a-808e-d45d9c8ae2d9/images/8db32338cc05912d2199b4dbe26128a0559def2e5cb7b5a51c16123f4ab4f316.jpeg",
  commercial: "https://static.prod-images.emergentagent.com/jobs/946b3673-7a0e-484a-808e-d45d9c8ae2d9/images/3e5af7d85ef50d0aa038a50eb8d8284172adb253bc7ff75ff237ae4cd7773085.jpeg",
  planning: "https://static.prod-images.emergentagent.com/jobs/946b3673-7a0e-484a-808e-d45d9c8ae2d9/images/0e9756300eb2b896fadea75e8d8540ab44346bc6b932a8ae358f95a00d2ebdc2.jpeg",
  turnkey: "https://static.prod-images.emergentagent.com/jobs/946b3673-7a0e-484a-808e-d45d9c8ae2d9/images/05c7d810a49f932dedf03d4a67da97358ef64e8a378bea820896565db5562f48.jpeg",
  render: "https://static.prod-images.emergentagent.com/jobs/946b3673-7a0e-484a-808e-d45d9c8ae2d9/images/c29518be164f7201db4e4e8fbcdf71da2e5cd29ea9104909bd4d16405aac9fca.jpeg",
  bedroom: "https://static.prod-images.emergentagent.com/jobs/946b3673-7a0e-484a-808e-d45d9c8ae2d9/images/53f927d8b7e8565f58dfb6f8573a990197dafcc9624e050318512a093553c60e.jpeg",
  kitchen: "https://static.prod-images.emergentagent.com/jobs/946b3673-7a0e-484a-808e-d45d9c8ae2d9/images/7e9f82dcc11fd53264335e966ce9f7910e5397cce87684f2335d9061e630bd08.jpeg",
};

const SERVICES = [
  {
    icon: Sofa,
    title: "Residential Interior Design",
    desc: "Homes that feel composed, not decorated — spatial planning, lighting, materials and furniture resolved as one idea.",
    img: IMG.residential,
    points: ["Space planning & material palettes", "Furniture, false ceiling & lighting design"],
  },
  {
    icon: Building2,
    title: "Commercial Interior Design",
    desc: "Offices, clinics, showrooms and cafés — branded environments that make clients trust you before you speak.",
    img: IMG.commercial,
    points: ["Brand-led workplace & retail design", "Ergonomics, acoustics and flow"],
  },
  {
    icon: DraftingCompass,
    title: "Architectural Planning",
    desc: "From plot to plan — layouts, elevations and municipal drawings prepared to build, not just to admire.",
    img: IMG.planning,
    points: ["Site planning & municipal submissions", "Structural and services coordination"],
  },
  {
    icon: KeyRound,
    title: "Turnkey Project Execution",
    desc: "One contract, one timeline, one team. We run the vendors, the site and the snags — you collect the keys.",
    img: IMG.turnkey,
    points: ["Single-point project management", "Budget & timeline accountability"],
  },
  {
    icon: Box,
    title: "3D Rendering",
    desc: "Photorealistic visuals of your space before a single wall is touched — decide with your eyes, not your imagination.",
    img: IMG.render,
    points: ["Photo-real interior & exterior renders", "Walkthrough animations on request"],
  },
];

const GALLERY = [
  { img: IMG.residential, label: "Residential Living" },
  { img: IMG.commercial, label: "Executive Boardroom" },
  { img: IMG.bedroom, label: "Master Suite" },
  { img: IMG.render, label: "Courtyard Villa — 3D Study" },
  { img: IMG.kitchen, label: "Designer Kitchen" },
  { img: IMG.turnkey, label: "Turnkey Lobby Lounge" },
];

const DESIGN_SERVICES = ["Residential Interior Design", "Commercial Interior Design", "Architectural Planning", "Turnkey Project Execution", "3D Rendering"];

export default function InteriorDesign() {
  return (
    <div data-testid="interior-page">
      <PageHero
        eyebrow="Spaces Studio — Ahmedabad"
        title={["Interior Design &", "Architecture."]}
        sub="Residential and commercial interiors, architectural planning, turnkey execution and 3D rendering — spaces designed as carefully as the wealth that funds them."
      >
        <ScrollCta href="#consultation" label="Book a Design Consultation" testId="design-consult-btn" />
      </PageHero>

      {/* SPLIT SHOWCASE */}
      <section className="py-16 sm:py-24 lg:py-28" data-testid="interior-services-section">
        <div className="container-x">
          <Reveal>
            <p className="eyebrow">The Practice</p>
            <h2 className="mt-3 max-w-2xl font-display text-3xl font-extrabold tracking-tight text-navy sm:text-4xl lg:text-5xl">
              Five disciplines. One signature.
            </h2>
          </Reveal>
          <div className="mt-14 space-y-16 sm:space-y-24">
            {SERVICES.map((s, i) => (
              <Reveal key={s.title} delay={0.05}>
                <div className={`grid grid-cols-1 items-center gap-8 lg:grid-cols-12 lg:gap-14 ${i % 2 === 1 ? "" : ""}`} data-testid={`interior-service-${i + 1}`}>
                  <div className={`lg:col-span-6 ${i % 2 === 1 ? "lg:order-2" : ""}`}>
                    <div className="group relative overflow-hidden rounded-3xl">
                      <img
                        src={s.img}
                        alt={s.title}
                        className="aspect-[4/3] w-full object-cover transition-transform duration-700 group-hover:scale-[1.04]"
                        loading="lazy"
                      />
                      <span className="absolute left-5 top-5 rounded-full bg-white/95 px-4 py-1.5 font-mono text-[11px] font-semibold uppercase tracking-[0.2em] text-navy backdrop-blur">
                        0{i + 1}
                      </span>
                    </div>
                  </div>
                  <div className={`lg:col-span-6 ${i % 2 === 1 ? "lg:order-1" : ""}`}>
                    <s.icon className="h-9 w-9 text-amber-600" />
                    <h3 className="mt-5 font-display text-2xl font-extrabold tracking-tight text-navy sm:text-3xl">{s.title}</h3>
                    <p className="mt-4 max-w-lg text-base leading-relaxed text-slate-600">{s.desc}</p>
                    <ul className="mt-5 space-y-2">
                      {s.points.map((pt) => (
                        <li key={pt} className="flex items-center gap-2.5 text-sm font-bold text-navy/80">
                          <span className="inline-block h-1.5 w-1.5 rotate-45 bg-gold" aria-hidden="true" />
                          {pt}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* PORTFOLIO */}
      <section className="border-y border-slate-200 bg-[#F8FAFC] py-16 sm:py-24" data-testid="interior-portfolio-section">
        <div className="container-x">
          <Reveal>
            <p className="eyebrow">Visual Portfolio</p>
            <h2 className="mt-3 font-display text-3xl font-extrabold tracking-tight text-navy sm:text-4xl">
              A look at the studio's language.
            </h2>
          </Reveal>
          <div className="mt-12 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {GALLERY.map((g, i) => (
              <Reveal key={g.label} delay={i * 0.06}>
                <div className="group relative overflow-hidden rounded-3xl" data-testid={`portfolio-tile-${i + 1}`}>
                  <img
                    src={g.img}
                    alt={g.label}
                    className="aspect-[4/3] w-full object-cover transition-transform duration-700 group-hover:scale-[1.05]"
                    loading="lazy"
                  />
                  <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-navy/85 via-navy/30 to-transparent p-5 pt-14">
                    <p className="font-display text-lg font-extrabold text-white">{g.label}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CONSULTATION */}
      <section id="consultation" className="grain relative bg-navy py-16 sm:py-24" data-testid="interior-consult-section">
        <div className="container-x relative z-10 grid grid-cols-1 items-center gap-12 lg:grid-cols-12">
          <Reveal className="lg:col-span-5">
            <p className="eyebrow !text-gold">Design Consultation</p>
            <h2 className="mt-3 font-display text-3xl font-extrabold tracking-tight text-white sm:text-4xl lg:text-5xl">
              Book a Design Consultation.
            </h2>
            <p className="mt-5 max-w-md text-white/70">
              Bring us a floor plan, a Pinterest board or just a frustration. The studio walks you through possibilities,
              budgets and timelines in the very first meeting.
            </p>
            <p className="mt-6 flex items-center gap-2 text-sm font-bold text-gold">
              Studio visits at our Ahmedabad office, by appointment <ArrowRight className="h-4 w-4" />
            </p>
          </Reveal>
          <Reveal delay={0.12} className="lg:col-span-7">
            <div className="rounded-3xl border border-white/10 bg-white p-6 shadow-2xl sm:p-8" data-testid="design-consult-form-card">
              <LeadForm
                source="interior-page"
                fields={["name", "phone", "email", "service", "message"]}
                services={DESIGN_SERVICES}
                cta="Book a Design Consultation"
                idPrefix="design-consult"
              />
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
